import type { User } from "firebase/auth";

const removeTrailingSlash = (value: string): string =>
  String(value || "").replace(/\/+$/, "");

const getBackendUrl = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();

    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0";

    if (isLocal) {
      return removeTrailingSlash("http://localhost:5000");
    }
  }

  return removeTrailingSlash("https://api.pickdengu.mw");
};

const BACKEND_URL = getBackendUrl();

export type MarketplaceEntityType =
  | "product"
  | "accommodation"
  | "car_hire"
  | "hike";

export type MarketplaceAvailability =
  | "in_stock"
  | "out_of_stock"
  | "preorder"
  | "unknown";

export type MarketplaceVerificationStatus =
  | "discovered"
  | "verified"
  | "stale"
  | "unavailable"
  | "blocked";

export type MarketplaceRunStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed";

export interface MarketplaceProduct {
  _id: string;
  title?: string;
  description?: string;
  brand?: string;
  category?: string;
  imageUrl?: string;
  thumbnail?: string;
  images?: string[];
  imageStoragePaths?: string[];
  price?: number;
  sourcePrice?: number;
  sourceCurrency?: string;
  status?: string;
  stockQty?: number;
  isFeatured?: boolean;
}

export interface MarketplaceListing {
  _id: string;
  ownerUserId: string;
  entityType?: MarketplaceEntityType;
  entityId?: string | null;
  sourceKey: string;
  externalId: string;
  retailer: string;
  retailerDomain?: string;
  sourceUrl: string;
  sourceImageUrl?: string;
  originalTitle: string;
  originalDescription?: string;
  observedPrice?: number | null;
  observedCurrency?: string;
  availability: MarketplaceAvailability;
  verificationStatus: MarketplaceVerificationStatus;
  stockQty?: number | null;
  imageUsageAllowed?: boolean;
  attributionRequired?: boolean;
  consecutiveMissingChecks?: number;
  consecutiveOutOfStockChecks?: number;
  lastObservedAt?: string;
  lastVerifiedAt?: string | null;
  product?: MarketplaceProduct | null;
  createdAt?: string;
  updatedAt?: string;
}

export type MarketplaceListingRow = MarketplaceListing;

export interface MarketplaceSourceStatus {
  sourceKey: string;
  enabled: boolean;
  description?: string;
}

export interface MarketplaceRun {
  _id: string;
  ownerUserId?: string;
  entityType?: MarketplaceEntityType;
  mode?: string;
  sourceKeys?: string[];
  queries?: string[];
  status: MarketplaceRunStatus;
  pipelineStage?: string;
  progressCurrent?: number;
  progressTotal?: number;
  progressMessage?: string;
  discovered: number;
  created: number;
  updated: number;
  skipped: number;
  checked?: number;
  unavailable?: number;
  archived?: number;
  failed: number;
  errors: string[];
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string | null;
}

export interface MarketplaceSyncRequest {
  entityType?: MarketplaceEntityType;
  queries: string[];
  sourceKeys?: string[];
  limit?: number;
  publish?: boolean;
  markupPercent?: number;
  generateImages?: boolean;
  country?: string;
  language?: string;
}

export interface MarketplaceSyncResult {
  runId: string;
  discovered: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface MarketplaceReconcileResult {
  runId: string;
  checked: number;
  updated: number;
  unavailable: number;
  archived: number;
  failed: number;
  errors: string[];
}

export interface MarketplaceStatusResult {
  sources: MarketplaceSourceStatus[];
  latestRuns: MarketplaceRun[];
}

export interface MarketplaceListingFilters {
  page?: number;
  limit?: number;
  entityType?: MarketplaceEntityType;
  sourceKey?: string;
  availability?: MarketplaceAvailability | "";
  verificationStatus?: MarketplaceVerificationStatus | "";
  includeIncomplete?: boolean;
}

export interface MarketplaceListingsResult {
  items: MarketplaceListing[];
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface MarketplaceBulkDeleteResult {
  requested: number;
  deleted: number;
  failed: number;
  errors: string[];
}

export interface MarketplaceBulkUpdateResult {
  requested: number;
  updated: number;
  failed: number;
  errors: string[];
}

export interface MarketplaceProductEditUpdate {
  title?: string;
  description?: string;
  brand?: string;
  category?: string;
  price?: number;
  stockQty?: number;
  status?: string;
  isFeatured?: boolean;
}

export interface MarketplaceProductEditResult {
  requestedId: string;
  productId: string;
  listingId?: string;
  updated: true;
  uploadedImages: number;
  product: MarketplaceProduct;
}

interface MarketplaceApiResponse<T> {
  status: string;
  data: T;
  message?: string;
  error?: string;
}

const getAuthHeaders = async (user: User): Promise<Record<string, string>> => {
  const token = await user.getIdToken(true);

  return {
    Authorization: `Bearer ${token}`,
  };
};

const getJsonHeaders = async (user: User): Promise<Record<string, string>> => ({
  ...(await getAuthHeaders(user)),
  "Content-Type": "application/json",
});

const parseResponse = async <T>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        `Request failed with status ${response.status}`,
    );
  }

  return payload as T;
};

export const editMarketplaceProduct = async (
  user: User,
  productOrListingId: string,
  update: MarketplaceProductEditUpdate,
  imageFiles: File[] = [],
  mainImageIndex = 0,
): Promise<MarketplaceProductEditResult> => {
  const formData = new FormData();

  formData.append("update", JSON.stringify(update || {}));
  formData.append("mainImageIndex", String(mainImageIndex));

  imageFiles.forEach((file) => {
    formData.append("images", file, file.name);
  });

  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/products/${encodeURIComponent(
      productOrListingId,
    )}/edit`,
    {
      method: "PATCH",
      headers: await getAuthHeaders(user),
      body: formData,
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceProductEditResult>>(
      response,
    );

  return payload.data;
};

export const searchAndSyncMarketplace = async (
  user: User,
  request: MarketplaceSyncRequest,
): Promise<MarketplaceSyncResult> => {
  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/search-sync`,
    {
      method: "POST",
      headers: await getJsonHeaders(user),
      body: JSON.stringify({
        entityType: request.entityType || "product",
        queries: request.queries,
        sourceKeys: request.sourceKeys || ["openai_web"],
        limit: request.limit ?? 10,
        publish: request.publish ?? true,
        markupPercent: request.markupPercent ?? 15,
        generateImages: request.generateImages ?? true,
        country: request.country || "ZA",
        language: request.language || "en",
      }),
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceSyncResult>>(
      response,
    );

  return payload.data;
};

export const runMarketplaceJob = async (
  user: User,
  input: {
    queries: string[];
    sourceKey: "openai_web" | "mock_public";
    limit: number;
    markupPercent: number;
  },
): Promise<MarketplaceSyncResult> => {
  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/manual-run`,
    {
      method: "POST",
      headers: await getJsonHeaders(user),
      body: JSON.stringify({
        entityType: "product",
        queries: input.queries,
        sourceKeys: [input.sourceKey],
        limit: input.limit,
        markupPercent: input.markupPercent,
        publish: true,
        generateImages: true,
        country: "ZA",
        language: "en",
      }),
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceSyncResult>>(
      response,
    );

  return payload.data;
};

export const getMarketplaceStatus = async (
  user: User,
): Promise<MarketplaceStatusResult> => {
  const response = await fetch(`${BACKEND_URL}/api/v1/ai/marketplace/status`, {
    method: "GET",
    headers: await getJsonHeaders(user),
  });

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceStatusResult>>(
      response,
    );

  return {
    sources: payload.data?.sources || [],
    latestRuns: payload.data?.latestRuns || [],
  };
};

export const getMarketplaceRun = async (
  user: User,
  runId: string,
): Promise<MarketplaceRun> => {
  const status = await getMarketplaceStatus(user);

  const run = status.latestRuns.find(
    (latestRun) => String(latestRun._id) === String(runId),
  );

  if (!run) {
    throw new Error(`Marketplace run ${runId} was not found.`);
  }

  return run;
};

export const getMarketplaceListings = async (
  user: User,
  filters: MarketplaceListingFilters = {},
): Promise<MarketplaceListingsResult> => {
  const params = new URLSearchParams();

  params.set("page", String(filters.page ?? 1));
  params.set("limit", String(filters.limit ?? 100));

  if (filters.entityType) {
    params.set("entityType", filters.entityType);
  }

  if (filters.sourceKey) {
    params.set("sourceKey", filters.sourceKey);
  }

  if (filters.availability) {
    params.set("availability", filters.availability);
  }

  if (filters.verificationStatus) {
    params.set("verificationStatus", filters.verificationStatus);
  }

  if (filters.includeIncomplete === true) {
    params.set("includeIncomplete", "true");
  }

  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/listings?${params.toString()}`,
    {
      method: "GET",
      headers: await getJsonHeaders(user),
    },
  );

  const payload =
    await parseResponse<
      MarketplaceApiResponse<MarketplaceListingsResult | MarketplaceListing[]>
    >(response);

  if (Array.isArray(payload.data)) {
    return {
      items: payload.data,
      page: filters.page ?? 1,
      limit: filters.limit ?? 100,
      total: payload.data.length,
      pages: payload.data.length > 0 ? 1 : 0,
    };
  }

  return {
    items: payload.data?.items || [],
    page: payload.data?.page || filters.page || 1,
    limit: payload.data?.limit || filters.limit || 100,
    total: payload.data?.total || 0,
    pages: payload.data?.pages || 0,
  };
};

export const getReliableMarketplaceListings = async (
  user: User,
): Promise<MarketplaceListing[]> => {
  const result = await getMarketplaceListings(user, {
    page: 1,
    limit: 100,
    includeIncomplete: false,
  });

  return result.items;
};

export const reconcileMarketplace = async (
  user: User,
  entityType: MarketplaceEntityType = "product",
): Promise<MarketplaceReconcileResult> => {
  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/reconcile`,
    {
      method: "POST",
      headers: await getJsonHeaders(user),
      body: JSON.stringify({
        entityType,
      }),
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceReconcileResult>>(
      response,
    );

  return payload.data;
};

export const bulkDeleteMarketplaceProducts = async (
  user: User,
  listingIds: string[],
): Promise<MarketplaceBulkDeleteResult> => {
  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/products/bulk-delete`,
    {
      method: "POST",
      headers: await getJsonHeaders(user),
      body: JSON.stringify({
        listingIds,
      }),
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceBulkDeleteResult>>(
      response,
    );

  return payload.data;
};

export const bulkUpdateMarketplaceProducts = async (
  user: User,
  listingIds: string[],
  update: Record<string, unknown>,
): Promise<MarketplaceBulkUpdateResult> => {
  const response = await fetch(
    `${BACKEND_URL}/api/v1/ai/marketplace/products/bulk-update`,
    {
      method: "PATCH",
      headers: await getJsonHeaders(user),
      body: JSON.stringify({
        listingIds,
        update,
      }),
    },
  );

  const payload =
    await parseResponse<MarketplaceApiResponse<MarketplaceBulkUpdateResult>>(
      response,
    );

  return payload.data;
};
